import React, { useMemo } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import ReactECharts from 'echarts-for-react'
import { Box, Flex, Icon, Loading } from '../../atoms'
import {
  GeographicDistributionChartProps,
  DEFAULT_GEOGRAPHIC_DISTRIBUTION_CHART_LABELS,
} from './GeographicDistributionChart.types'
import {
  formatCurrency,
  formatNumber,
  getSwissCantonColor,
} from './GeographicDistributionChart.utils'

const DashboardContainer = styled.div`
  width: 100%;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`

const MetricCard = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: center;
`

const MetricValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 8px 0;
`

const MetricLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const ChartContainer = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  min-height: 400px;
`

const TopCitiesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`

const CityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
`

const CityName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`

const CityStats = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const StatValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const GeographicDistributionChart: React.FC<
  GeographicDistributionChartProps
> = ({ data, loading = false, error = null, labels = {} }) => {
  const mergedLabels = {
    ...DEFAULT_GEOGRAPHIC_DISTRIBUTION_CHART_LABELS,
    ...labels,
  }
  const cantonBarChartOption = useMemo(() => {
    if (!data || !data.byCanton || data.byCanton.length === 0) {
      return null
    }

    const sortedCantons = [...data.byCanton]
      .sort((a, b) => b.count - a.count)
      .slice(0, 15) // Top 15 cantons

    return {
      title: {
        text: mergedLabels.transactionsByCanton,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0]
          return `
            <div style="padding: 8px;">
              <strong>${data.name}</strong><br/>
              Transactions: ${formatNumber(data.value)}<br/>
              Avg Price: ${formatCurrency(sortedCantons[data.dataIndex].avgPrice)}<br/>
              Total Volume: ${formatCurrency(sortedCantons[data.dataIndex].totalValue)}
            </div>
          `
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatNumber(value),
        },
      },
      yAxis: {
        type: 'category',
        data: sortedCantons.map((c) => c.name).reverse(),
        axisLabel: {
          fontSize: 11,
        },
      },
      series: [
        {
          name: 'Transactions',
          type: 'bar',
          data: sortedCantons.map((c) => c.count).reverse(),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#e3f2fd' },
              { offset: 1, color: '#1976d2' },
            ]),
          },
        },
      ],
    }
  }, [data])

  if (loading) {
    return (
      <DashboardContainer>
        <Flex justify="center" align="center" style={{ minHeight: '400px' }}>
          <Loading size="large" />
        </Flex>
      </DashboardContainer>
    )
  }

  if (error || !data) {
    return (
      <DashboardContainer>
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: '400px', color: '#999' }}
        >
          {error || mergedLabels.noData}
        </Flex>
      </DashboardContainer>
    )
  }

  // Calculate key metrics
  const topCanton = data.byCanton[0]
  const totalVolume = data.byCanton.reduce((sum, c) => sum + c.totalValue, 0)
  // Calculate total transactions from canton data if not provided or is 0
  const totalTransactions =
    data.totalTransactions || data.byCanton.reduce((sum, c) => sum + c.count, 0)
  const avgTransactionPrice =
    totalTransactions > 0 ? totalVolume / totalTransactions : 0

  return (
    <DashboardContainer>
      <MetricsGrid>
        <MetricCard>
          <Icon name="map-pin" size="medium" />
          <MetricValue>{data.byCanton.length}</MetricValue>
          <MetricLabel>{mergedLabels.activeCantons}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="building" size="medium" />
          <MetricValue>{formatNumber(totalTransactions)}</MetricValue>
          <MetricLabel>{mergedLabels.totalTransactions}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="trending-up" size="medium" />
          <MetricValue>{formatCurrency(avgTransactionPrice)}</MetricValue>
          <MetricLabel>{mergedLabels.avgTransactionPrice}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="bar-chart" size="medium" />
          <MetricValue>{formatCurrency(totalVolume)}</MetricValue>
          <MetricLabel>{mergedLabels.totalMarketVolume}</MetricLabel>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        {cantonBarChartOption && (
          <ChartContainer>
            <ReactECharts
              option={cantonBarChartOption}
              style={{ height: '500px' }}
            />
          </ChartContainer>
        )}

        <ChartContainer>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            {mergedLabels.topCitiesByVolume}
          </h3>
          <TopCitiesList>
            {data.topCities.map((city, index) => (
              <CityRow key={city.name}>
                <Flex align="center" gap={12}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#1976d2',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {index + 1}
                  </div>
                  <CityName>{city.name}</CityName>
                </Flex>
                <CityStats>
                  <div>
                    <StatValue>{formatNumber(city.count)}</StatValue>{' '}
                    {mergedLabels.transactions}
                  </div>
                  <div>
                    {mergedLabels.avg}:{' '}
                    <StatValue>{formatCurrency(city.avgPrice)}</StatValue>
                  </div>
                </CityStats>
              </CityRow>
            ))}
          </TopCitiesList>
        </ChartContainer>
      </ChartsGrid>
    </DashboardContainer>
  )
}
